import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  registerUserScopedStore,
  userScopedStorage,
} from '@/shared/lib/user-scoped-storage';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ViewDensity = 'comfortable' | 'compact' | 'spacious';
export type ColorScheme = 'default' | 'blue' | 'green' | 'purple' | 'orange';
export type FontSize = 'small' | 'medium' | 'large';

interface ThemeSettings {
  mode: ThemeMode;
  colorScheme: ColorScheme;
  fontSize: FontSize;
  reducedMotion: boolean;
  highContrast: boolean;
}

export type CollapsibleSectionId =
  | 'projectGoals'
  | 'projectAngles'
  | 'projectReferences';

interface LayoutPreferences {
  viewDensity: ViewDensity;
  showBreadcrumbs: boolean;
  showStatusBar: boolean;
  gridViewColumns: number;
  listViewDensity: 'compact' | 'normal' | 'spacious';
  collapsedSections: Record<CollapsibleSectionId, boolean>;
}

interface UserCustomizations {
  favoriteProjects: string[];
  recentColors: string[];
  customShortcuts: Record<string, string>;
  dashboardLayout: 'grid' | 'cards';
  defaultRenderSettings: {
    quality: 'draft' | 'standard' | 'high';
    autoSave: boolean;
    showNotifications: boolean;
  };
  workspacePreferences: {
    autoOpenLastProject: boolean;
    confirmBeforeDelete: boolean;
    autoSaveInterval: number;
    showTips: boolean;
  };
}

interface PreferencesState {
  theme: ThemeSettings;
  layout: LayoutPreferences;
  customizations: UserCustomizations;

  ui: {
    isEditingPreferences: boolean;
    preferencesPanel: 'theme' | 'layout' | 'customization' | null;
    unsavedChanges: boolean;
    lastSaved: Date | null;
    error: string | null;
  };
}

interface PreferencesActions {
  // Theme actions
  setThemeMode: (mode: ThemeMode) => void;
  setColorScheme: (scheme: ColorScheme) => void;
  setFontSize: (size: FontSize) => void;
  toggleReducedMotion: () => void;
  toggleHighContrast: () => void;
  applyTheme: () => void;

  // Layout actions
  setViewDensity: (density: ViewDensity) => void;
  toggleBreadcrumbs: () => void;
  toggleStatusBar: () => void;
  setGridColumns: (columns: number) => void;
  setListDensity: (density: 'compact' | 'normal' | 'spacious') => void;
  toggleSectionCollapsed: (sectionId: CollapsibleSectionId) => void;
  isSectionCollapsed: (sectionId: CollapsibleSectionId) => boolean;

  // Customization actions
  addFavoriteProject: (projectId: string) => void;
  removeFavoriteProject: (projectId: string) => void;
  toggleFavoriteProject: (projectId: string) => void;
  addRecentColor: (color: string) => void;
  setCustomShortcut: (action: string, shortcut: string) => void;
  removeCustomShortcut: (action: string) => void;
  setDashboardLayout: (layout: 'grid' | 'cards') => void;

  // Render settings
  setDefaultQuality: (quality: 'draft' | 'standard' | 'high') => void;
  toggleAutoSave: () => void;
  toggleRenderNotifications: () => void;

  // Workspace preferences
  toggleAutoOpenLastProject: () => void;
  toggleConfirmDelete: () => void;
  setAutoSaveInterval: (interval: number) => void;
  toggleShowTips: () => void;

  // UI actions
  openPreferencesPanel: (panel: 'theme' | 'layout' | 'customization') => void;
  closePreferencesPanel: () => void;
  setUnsavedChanges: (hasChanges: boolean) => void;
  savePreferences: () => void;

  // Computed values
  getEffectiveTheme: () => 'light' | 'dark';
  isFavoriteProject: (projectId: string) => boolean;
  getShortcutForAction: (action: string) => string | undefined;

  // Import/Export
  exportPreferences: () => string;
  importPreferences: (json: string) => void;

  // Reset
  resetTheme: () => void;
  resetLayout: () => void;
  resetCustomizations: () => void;
  resetAll: () => void;
}

type PreferencesStore = PreferencesState & PreferencesActions;

const defaultTheme: ThemeSettings = {
  mode: 'system',
  colorScheme: 'default',
  fontSize: 'medium',
  reducedMotion: false,
  highContrast: false,
};

const defaultLayout: LayoutPreferences = {
  viewDensity: 'comfortable',
  showBreadcrumbs: true,
  showStatusBar: true,
  gridViewColumns: 3,
  listViewDensity: 'normal',
  collapsedSections: {
    projectGoals: false,
    projectAngles: false,
    projectReferences: false,
  },
};

const defaultCustomizations: UserCustomizations = {
  favoriteProjects: [],
  recentColors: [],
  customShortcuts: {},
  dashboardLayout: 'grid',
  defaultRenderSettings: {
    quality: 'standard',
    autoSave: true,
    showNotifications: true,
  },
  workspacePreferences: {
    autoOpenLastProject: false,
    confirmBeforeDelete: true,
    autoSaveInterval: 60,
    showTips: true,
  },
};

const defaultUIState: PreferencesState['ui'] = {
  isEditingPreferences: false,
  preferencesPanel: null,
  unsavedChanges: false,
  lastSaved: null,
  error: null,
};

export const usePreferencesStore = create<PreferencesStore>()(
  subscribeWithSelector(
    persist(
      immer((set, get) => ({
        // Initial state
        theme: defaultTheme,
        layout: defaultLayout,
        customizations: defaultCustomizations,
        ui: defaultUIState,

        // Theme actions
        setThemeMode: (mode) =>
          set((state) => {
            state.theme.mode = mode;
            state.ui.unsavedChanges = true;
            get().applyTheme();
          }),

        setColorScheme: (scheme) =>
          set((state) => {
            state.theme.colorScheme = scheme;
            state.ui.unsavedChanges = true;
          }),

        setFontSize: (size) =>
          set((state) => {
            state.theme.fontSize = size;
            state.ui.unsavedChanges = true;
          }),

        toggleReducedMotion: () =>
          set((state) => {
            state.theme.reducedMotion = !state.theme.reducedMotion;
            state.ui.unsavedChanges = true;
          }),

        toggleHighContrast: () =>
          set((state) => {
            state.theme.highContrast = !state.theme.highContrast;
            state.ui.unsavedChanges = true;
          }),

        applyTheme: () => {
          const state = get();
          const effectiveTheme = state.getEffectiveTheme();

          if (typeof window !== 'undefined') {
            const root = document.documentElement;

            // Apply theme mode
            root.classList.toggle('dark', effectiveTheme === 'dark');

            // Apply color scheme
            root.setAttribute('data-color-scheme', state.theme.colorScheme);

            // Apply font size
            root.setAttribute('data-font-size', state.theme.fontSize);

            // Apply accessibility settings
            root.classList.toggle('reduced-motion', state.theme.reducedMotion);
            root.classList.toggle('high-contrast', state.theme.highContrast);
          }
        },

        // Layout actions
        setViewDensity: (density) =>
          set((state) => {
            state.layout.viewDensity = density;
            state.ui.unsavedChanges = true;
          }),

        toggleBreadcrumbs: () =>
          set((state) => {
            state.layout.showBreadcrumbs = !state.layout.showBreadcrumbs;
            state.ui.unsavedChanges = true;
          }),

        toggleStatusBar: () =>
          set((state) => {
            state.layout.showStatusBar = !state.layout.showStatusBar;
            state.ui.unsavedChanges = true;
          }),

        setGridColumns: (columns) =>
          set((state) => {
            state.layout.gridViewColumns = Math.min(Math.max(columns, 1), 6);
            state.ui.unsavedChanges = true;
          }),

        setListDensity: (density) =>
          set((state) => {
            state.layout.listViewDensity = density;
            state.ui.unsavedChanges = true;
          }),

        toggleSectionCollapsed: (sectionId) =>
          set((state) => {
            state.layout.collapsedSections[sectionId] =
              !state.layout.collapsedSections[sectionId];
          }),

        isSectionCollapsed: (sectionId) => {
          const state = get();
          return state.layout.collapsedSections[sectionId] ?? false;
        },

        // Customization actions
        addFavoriteProject: (projectId) =>
          set((state) => {
            if (!state.customizations.favoriteProjects.includes(projectId)) {
              state.customizations.favoriteProjects.push(projectId);
              state.ui.unsavedChanges = true;
            }
          }),

        removeFavoriteProject: (projectId) =>
          set((state) => {
            const index =
              state.customizations.favoriteProjects.indexOf(projectId);
            if (index > -1) {
              state.customizations.favoriteProjects.splice(index, 1);
              state.ui.unsavedChanges = true;
            }
          }),

        toggleFavoriteProject: (projectId) => {
          const state = get();
          if (state.customizations.favoriteProjects.includes(projectId)) {
            state.removeFavoriteProject(projectId);
          } else {
            state.addFavoriteProject(projectId);
          }
        },

        addRecentColor: (color) =>
          set((state) => {
            // Remove if exists and add to front
            const colors = state.customizations.recentColors.filter(
              (c) => c !== color
            );
            colors.unshift(color);
            // Keep only last 10 colors
            state.customizations.recentColors = colors.slice(0, 10);
            state.ui.unsavedChanges = true;
          }),

        setCustomShortcut: (action, shortcut) =>
          set((state) => {
            state.customizations.customShortcuts[action] = shortcut;
            state.ui.unsavedChanges = true;
          }),

        removeCustomShortcut: (action) =>
          set((state) => {
            delete state.customizations.customShortcuts[action];
            state.ui.unsavedChanges = true;
          }),

        setDashboardLayout: (layout) =>
          set((state) => {
            state.customizations.dashboardLayout = layout;
            state.ui.unsavedChanges = true;
          }),

        // Render settings
        setDefaultQuality: (quality) =>
          set((state) => {
            state.customizations.defaultRenderSettings.quality = quality;
            state.ui.unsavedChanges = true;
          }),

        toggleAutoSave: () =>
          set((state) => {
            state.customizations.defaultRenderSettings.autoSave =
              !state.customizations.defaultRenderSettings.autoSave;
            state.ui.unsavedChanges = true;
          }),

        toggleRenderNotifications: () =>
          set((state) => {
            state.customizations.defaultRenderSettings.showNotifications =
              !state.customizations.defaultRenderSettings.showNotifications;
            state.ui.unsavedChanges = true;
          }),

        // Workspace preferences
        toggleAutoOpenLastProject: () =>
          set((state) => {
            state.customizations.workspacePreferences.autoOpenLastProject =
              !state.customizations.workspacePreferences.autoOpenLastProject;
            state.ui.unsavedChanges = true;
          }),

        toggleConfirmDelete: () =>
          set((state) => {
            state.customizations.workspacePreferences.confirmBeforeDelete =
              !state.customizations.workspacePreferences.confirmBeforeDelete;
            state.ui.unsavedChanges = true;
          }),

        setAutoSaveInterval: (interval) =>
          set((state) => {
            state.customizations.workspacePreferences.autoSaveInterval =
              Math.min(Math.max(interval, 10), 300);
            state.ui.unsavedChanges = true;
          }),

        toggleShowTips: () =>
          set((state) => {
            state.customizations.workspacePreferences.showTips =
              !state.customizations.workspacePreferences.showTips;
            state.ui.unsavedChanges = true;
          }),

        // UI actions
        openPreferencesPanel: (panel) =>
          set((state) => {
            state.ui.isEditingPreferences = true;
            state.ui.preferencesPanel = panel;
          }),

        closePreferencesPanel: () =>
          set((state) => {
            state.ui.isEditingPreferences = false;
            state.ui.preferencesPanel = null;
          }),

        setUnsavedChanges: (hasChanges) =>
          set((state) => {
            state.ui.unsavedChanges = hasChanges;
          }),

        savePreferences: () =>
          set((state) => {
            state.ui.unsavedChanges = false;
            state.ui.lastSaved = new Date();
            get().applyTheme();
          }),

        // Computed values
        getEffectiveTheme: () => {
          const state = get();
          if (state.theme.mode === 'system') {
            if (typeof window !== 'undefined') {
              return window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';
            }
            return 'light';
          }
          return state.theme.mode;
        },

        isFavoriteProject: (projectId) => {
          const state = get();
          return state.customizations.favoriteProjects.includes(projectId);
        },

        getShortcutForAction: (action) => {
          const state = get();
          return state.customizations.customShortcuts[action];
        },

        // Import/Export
        exportPreferences: () => {
          const state = get();
          return JSON.stringify(
            {
              theme: state.theme,
              layout: state.layout,
              customizations: state.customizations,
            },
            null,
            2
          );
        },

        importPreferences: (json) => {
          try {
            const data = JSON.parse(json);
            set((state) => {
              if (data.theme) state.theme = { ...defaultTheme, ...data.theme };
              if (data.layout)
                state.layout = { ...defaultLayout, ...data.layout };
              if (data.customizations) {
                state.customizations = {
                  ...defaultCustomizations,
                  ...data.customizations,
                };
              }
              state.ui.unsavedChanges = true;
            });
            get().applyTheme();
          } catch (_error) {
            set((state) => {
              state.ui.error = 'Failed to import preferences: Invalid format';
            });
          }
        },

        // Reset functions
        resetTheme: () =>
          set((state) => {
            state.theme = { ...defaultTheme };
            state.ui.unsavedChanges = true;
            get().applyTheme();
          }),

        resetLayout: () =>
          set((state) => {
            state.layout = { ...defaultLayout };
            state.ui.unsavedChanges = true;
          }),

        resetCustomizations: () =>
          set((state) => {
            state.customizations = { ...defaultCustomizations };
            state.ui.unsavedChanges = true;
          }),

        resetAll: () =>
          set((state) => {
            state.theme = { ...defaultTheme };
            state.layout = { ...defaultLayout };
            state.customizations = { ...defaultCustomizations };
            state.ui = { ...defaultUIState };
            get().applyTheme();
          }),
      })),
      {
        name: 'ui-preferences',
        // User-scoped key prefix: see `user-scoped-storage.ts`. Without this,
        // workspace layout, theme overrides, and default render settings of
        // user A leak into user B's session on a shared device.
        storage: createJSONStorage(() => userScopedStorage()),
        partialize: (state) => ({
          theme: state.theme,
          layout: state.layout,
          customizations: state.customizations,
        }),
        merge: (persistedState, currentState) => {
          const persisted = persistedState as
            | Partial<PreferencesState>
            | undefined;
          return {
            ...currentState,
            theme: { ...defaultTheme, ...persisted?.theme },
            layout: {
              ...defaultLayout,
              ...persisted?.layout,
              // Deep merge collapsedSections to ensure all keys exist
              collapsedSections: {
                ...defaultLayout.collapsedSections,
                ...persisted?.layout?.collapsedSections,
              },
            },
            customizations: {
              ...defaultCustomizations,
              ...persisted?.customizations,
              defaultRenderSettings: {
                ...defaultCustomizations.defaultRenderSettings,
                ...persisted?.customizations?.defaultRenderSettings,
              },
              workspacePreferences: {
                ...defaultCustomizations.workspacePreferences,
                ...persisted?.customizations?.workspacePreferences,
              },
            },
          };
        },
        onRehydrateStorage: () => (state) => {
          // Apply theme on rehydration
          state?.applyTheme();
        },
      }
    )
  )
);

registerUserScopedStore(usePreferencesStore);
